import React, { useMemo } from 'react';
import { Category, CategoryEntry } from './types';
import { CategoriesByLevels } from './CategoriesByLevels';
import { CategoryTreeView } from './CategoryTreeView';
import { semantic_colors, shapes, Stack } from '../..';
import { css } from '@emotion/react';
import classNames from 'classnames';
import { getCategoryMaxDepth } from './utils';

interface BaseCategorySelectorProps {
  className?: string;
  selectedKeys: Array<string>;
  /**
   * 선택값 변경 이벤트
   * @param selectedKeys 선택된 카테고리 키 리스트
   * @param isLeaf selectedKeys의 리프 여부 (multiple == false 일 때만 내려감)
   */
  onSelectedKeysChange: (selectedKeys: Array<string>, isLeaf?: boolean) => void;
  /**
   * 체크박스 사용여부
   * true일 경우 멀티 셀렉트 가능
   * @default false
   */
  multiple?: boolean;
  /** @default false */
  disabled?: boolean;
  /**
   * 각 ListItem의 너비
   * @default 240
   */
  width?: number;
  /**
   * 각 List의 높이
   * @default 332
   */
  height?: number;
}

interface LegacyCategorySelectorProps extends BaseCategorySelectorProps {
  /**
   * 레벨별 카테고리 데이터
   * @deprecated items를 사용해주세요. 이 방식은 향후 제거될 예정입니다.
   */
  categoriesByLevels: Array<Array<Category>>;
  items?: never; // items 사용 금지
}

export interface NewCategorySelectorProps extends BaseCategorySelectorProps {
  /**
   * 트리 구조 카테고리 데이터
   */
  items: CategoryEntry[];
  categoriesByLevels?: never; // categoriesByLevels 사용 금지
}

export type CategorySelectorProps = LegacyCategorySelectorProps | NewCategorySelectorProps;

function isLegacyProps(props: CategorySelectorProps): props is LegacyCategorySelectorProps {
  return 'categoriesByLevels' in props;
}
function isNewProps(props: CategorySelectorProps): props is NewCategorySelectorProps {
  return 'items' in props;
}
function validateProps(props: CategorySelectorProps): void {
  const hasItems = 'items' in props && props.items !== undefined;
  const hasCategoriesByLevels = 'categoriesByLevels' in props && props.categoriesByLevels !== undefined;

  if (hasItems && hasCategoriesByLevels) {
    throw new Error(
      'CategorySelector: items와 categoriesByLevels를 동시에 사용할 수 없습니다. items (권장)을 사용해주세요.',
    );
  }
}

export const CategorySelector = (props: CategorySelectorProps) => {
  validateProps(props);
  const {
    className,
    selectedKeys,
    onSelectedKeysChange,
    multiple = false,
    disabled = false,
    width = 240,
    height = 332,
  } = props;
  const isLegacyMode = isLegacyProps(props);
  const categoriesByLevels = isLegacyMode ? props.categoriesByLevels : undefined;
  const items = useMemo((): CategoryEntry[] => {
    return isNewProps(props) ? props.items : [];
  }, [props]);

  const maxDepth = useMemo(
    () => (!isLegacyMode && items.length > 0 ? getCategoryMaxDepth(items) : 1),
    [isLegacyMode, items],
  );

  return (
    <Stack
      direction='row'
      className={classNames(['pds-category-selector', className])}
      width={categoriesByLevels ? width * categoriesByLevels.length : width * maxDepth}
      height={height}
      css={css`
        overflow: hidden;
        border: 1px solid ${semantic_colors.border.primary};
        ${shapes.border_radius.small}
      `}
    >
      {isLegacyMode ? (
        <CategoriesByLevels
          categoriesByLevels={categoriesByLevels!}
          selectedKeys={selectedKeys}
          multiple={multiple}
          disabled={disabled}
          onSelectedKeysChange={onSelectedKeysChange}
        />
      ) : (
        <CategoryTreeView
          items={items}
          selectedKeys={selectedKeys}
          onSelectedKeysChange={onSelectedKeysChange}
          multiple={multiple}
          disabled={disabled}
          width={width}
          maxDepth={maxDepth}
        />
      )}
    </Stack>
  );
};
